/**
 * Input primitive -- Leviathan design system.
 *
 * Focus ring reads from design tokens:
 *   no accent  -> color.gold (--color-gold)
 *   accent prop -> pillarAccent[accent]
 *
 * `accent` prop: 'leviathan' | 'ouroboros' | 'apophis' | 'crossCutting'
 *
 * Do not add em-dashes anywhere.
 */

import * as React from 'react'
import { Input as InputPrimitive } from '@base-ui/react/input'

import { cn } from '@/lib/utils'
import { pillarAccent, color, type Pillar } from '@/design-system/tokens'

export interface InputProps extends React.ComponentProps<'input'> {
  accent?: Pillar
}

function Input({ className, type, accent, style, ...props }: InputProps) {
  // Resolve the focus-ring color from tokens.
  const ringColor = accent ? pillarAccent[accent] : color.gold

  return (
    <InputPrimitive
      type={type}
      data-slot="input"
      data-accent={accent}
      className={cn(
        'h-8 w-full min-w-0 rounded-lg border border-white/10 bg-white/4 px-2.5 py-1 text-base text-foreground transition-colors outline-none',
        'file:inline-flex file:h-6 file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground',
        'placeholder:text-muted-foreground',
        'focus-visible:ring-2',
        'disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
        'aria-invalid:border-destructive aria-invalid:ring-destructive/20',
        'md:text-sm',
        className,
      )}
      style={{
        // CSS custom prop trick: set the ring color as a variable so Tailwind
        // `ring-*` can pick it up. We fall back to inline box-shadow on focus
        // because arbitrary color values in Tailwind v4 require config.
        '--input-ring': ringColor,
        ...style,
      } as React.CSSProperties}
      onFocus={(e) => {
        e.currentTarget.style.borderColor = `${ringColor}60`
        e.currentTarget.style.boxShadow = `0 0 0 3px ${ringColor}25`
        if (props.onFocus) props.onFocus(e)
      }}
      onBlur={(e) => {
        e.currentTarget.style.borderColor = ''
        e.currentTarget.style.boxShadow = ''
        if (props.onBlur) props.onBlur(e)
      }}
      {...props}
    />
  )
}

export { Input }
